import { Row, Table } from '@tanstack/react-table';
import { ColumnGroup, CustomColumnDef } from '../types';
import { getColumnId } from '../utils';

const isMissingSortValue = (v: unknown): boolean =>
  v == null || (typeof v === 'number' && Number.isNaN(v));

// Missing values always sort last, in both directions: check before `desc` negation.
function compareDefaultValues(a: unknown, b: unknown, desc: boolean): number {
  const aMissing = isMissingSortValue(a);
  const bMissing = isMissingSortValue(b);
  if (aMissing || bMissing) {
    if (aMissing && bMissing) return 0;
    return aMissing ? 1 : -1;
  }
  const base =
    typeof a === 'number' && typeof b === 'number'
      ? a < b
        ? -1
        : a > b
          ? 1
          : 0
      : String(a).localeCompare(String(b), undefined, { numeric: true });
  return desc ? -base : base;
}

function buildCurrentSortingComparator<TData, TValue>(
  table: Table<TData>,
  finalColumnGroups: ColumnGroup<TData, TValue>[],
  getOriginal: (row: unknown) => TData
): ((a: unknown, b: unknown) => number) | null {
  const sorting = table.getState().sorting || [];

  if (!sorting.length) return null;

  const columnDefById = new Map<string, CustomColumnDef<TData, TValue>>();

  finalColumnGroups.forEach((group) => {
    group.columns.forEach((col) => columnDefById.set(getColumnId(col), col));
  });

  const comparators = sorting.map(({ id, desc }) => {
    const col = columnDefById.get(id);
    const getValue = (row: unknown) => {
      const original = getOriginal(row);
      const data = original as unknown as Record<string, unknown>;
      if (!col) return data[id];
      if ('accessorKey' in col && col.accessorKey)
        return data[col.accessorKey as string];
      if ('accessorFn' in col && typeof col.accessorFn === 'function')
        return (col as any).accessorFn(original);
      return data[id];
    };

    const userSort = col?.sort?.sortingFn as
      | ((a: unknown, b: unknown) => number)
      | undefined;
    return (a: unknown, b: unknown) => {
      const av = getValue(a) as unknown;
      const bv = getValue(b) as unknown;
      if (userSort) return desc ? -userSort(av, bv) : userSort(av, bv);
      return compareDefaultValues(av, bv, desc);
    };
  });

  return (a: unknown, b: unknown) => {
    for (let i = 0; i < comparators.length; i += 1) {
      const res = comparators[i](a, b);
      if (res !== 0) return res;
    }
    return 0;
  };
}

export function createSortRowsForCurrentSorting<TData, TValue>(
  table: Table<TData>,
  finalColumnGroups: ColumnGroup<TData, TValue>[],
  isClientSortingDisabled = false
) {
  return (rows: TData[]): TData[] => {
    if (isClientSortingDisabled) return rows;
    const cmp = buildCurrentSortingComparator(
      table,
      finalColumnGroups,
      (r) => r as TData
    );
    if (!cmp) return rows;
    return [...rows].sort(cmp as (a: TData, b: TData) => number);
  };
}

export function createSortReactTableRowsForCurrentSorting<TData, TValue>(
  table: Table<TData>,
  finalColumnGroups: ColumnGroup<TData, TValue>[],
  isClientSortingDisabled = false
) {
  return (rows: Row<TData>[]): Row<TData>[] => {
    if (isClientSortingDisabled) return rows;
    const cmp = buildCurrentSortingComparator(
      table,
      finalColumnGroups,
      (r) => (r as Row<TData>).original
    );
    if (!cmp) return rows;
    return [...rows].sort(cmp as (a: Row<TData>, b: Row<TData>) => number);
  };
}
